// Login / registration page (SPEC-391): centered narrow card with brand, login form, and an
// invite-flow registration mode. Errors and the "pending approval" notice render inline.

import { useState } from 'react';
import { postLogin, postRegister } from '../api';
import { useT, toggleLang } from '../i18n';
import { useTheme, toggleTheme } from '../theme';

export interface LoginPageProps {
  expired: boolean;
  onAuthed: () => void;
}

export default function LoginPage({ expired, onAuthed }: LoginPageProps) {
  const t = useT();
  const theme = useTheme();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [invite, setInvite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const switchMode = (m: 'login' | 'register') => {
    setMode(m);
    setError(null);
    setNotice(null);
  };

  const submit = async () => {
    if (busy || !username.trim() || !password) return;
    setBusy(true);
    setError(null);
    if (mode === 'login') {
      const r = await postLogin(username.trim(), password);
      setBusy(false);
      if (r === 'ok') { onAuthed(); return; }
      setError(t(r === 'bad_credentials' ? 'login.badCredentials' : r === 'not_configured' ? 'login.notConfigured' : 'login.networkError'));
      return;
    }
    // register
    const r = await postRegister(username.trim(), password, invite);
    setBusy(false);
    if (r === 'ok') { onAuthed(); return; }
    if (r === 'pending') {
      setNotice(t('login.pendingNotice'));
      setMode('login');
      setPassword('');
      return;
    }
    setError(t(
      r === 'bad_username' ? 'login.badUsername'
      : r === 'bad_password' ? 'login.badPassword'
      : r === 'exists' ? 'login.userExists'
      : r === 'bad_invite' ? 'login.badInvite'
      : r === 'not_configured' ? 'login.notConfigured'
      : 'login.networkError'
    ));
  };

  return (
    <div className="login-wrap">
      <div className="login-toggles">
        <button className="ghost-btn" onClick={() => toggleLang()} title={t('topbar.lang.title')} aria-label={t('topbar.lang.title')}>
          {t('topbar.lang.label')}
        </button>
        <button
          className="ghost-btn icon-btn"
          onClick={() => toggleTheme()}
          title={t(theme === 'dark' ? 'topbar.theme.toLight' : 'topbar.theme.toDark')}
          aria-label={t(theme === 'dark' ? 'topbar.theme.toLight' : 'topbar.theme.toDark')}
        >
          {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
        </button>
      </div>
      <div className="login-card">
        <h1 className="login-brand">design place</h1>
        <p className="login-sub">{mode === 'login' ? t('login.subtitle') : t('login.registerSubtitle')}</p>
        <form
          onSubmit={e => { e.preventDefault(); void submit(); }}
        >
          <label className="field-label" htmlFor="login-username">{t('login.username')}</label>
          <input
            id="login-username"
            className="text-input"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoFocus
            autoComplete="username"
          />
          <label className="field-label" htmlFor="login-password">{t('login.password')}</label>
          <input
            id="login-password"
            className="text-input"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
          {mode === 'register' && (
            <>
              <label className="field-label" htmlFor="login-invite">{t('login.invite')}</label>
              <input
                id="login-invite"
                className="text-input"
                value={invite}
                onChange={e => setInvite(e.target.value)}
                autoComplete="off"
                placeholder={t('login.invitePlaceholder')}
              />
            </>
          )}
          {expired && !error && !notice && <p className="login-notice">{t('login.expired')}</p>}
          {notice && <p className="login-notice">{notice}</p>}
          {error && <p className="login-error">{error}</p>}
          <button className="primary-btn login-submit" type="submit" disabled={busy || !username.trim() || !password}>
            {busy ? t('login.submitting') : mode === 'login' ? t('login.submit') : t('login.registerSubmit')}
          </button>
        </form>
        <button
          className="login-switch"
          onClick={() => switchMode(mode === 'login' ? 'register' : 'login')}
        >
          {mode === 'login' ? t('login.noAccount') : t('login.haveAccount')}
        </button>
      </div>
    </div>
  );
}

export function SunIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

export function MoonIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}
